import { useState } from 'react';
import type { FiltrosLeads, OpcoesLeads } from '../types';
import { filtrosVazios } from '../utils';
import SeletorFiltro from './SeletorFiltro';

interface FiltrosPublicoProps {
  opcoes: OpcoesLeads;
  grupo: FiltrosLeads;
  onChange: (grupo: FiltrosLeads) => void;
}

// Bloco "Para quem?": escolhe entre todos ou um grupo montado por eventos,
// cidades e tipo de pessoa. Devolve o grupo escolhido (vazio = todos).
export default function FiltrosPublico({ opcoes, grupo, onChange }: FiltrosPublicoProps) {
  const [modo, setModo] = useState<'todos' | 'grupo'>(() =>
    filtrosVazios(grupo) ? 'todos' : 'grupo',
  );

  const escolhidos = [
    ...opcoes.eventos
      .filter((o) => grupo.eventos.includes(Number(o.valor)))
      .map((o) => ({ tipo: 'eventos' as const, valor: o.valor, rotulo: o.rotulo })),
    ...opcoes.cidades
      .filter((o) => grupo.cidades.includes(o.valor))
      .map((o) => ({ tipo: 'cidades' as const, valor: o.valor, rotulo: o.rotulo })),
    ...opcoes.origens
      .filter((o) => grupo.origens.includes(o.valor))
      .map((o) => ({ tipo: 'origens' as const, valor: o.valor, rotulo: o.rotulo })),
  ];

  const remover = (tipo: 'eventos' | 'cidades' | 'origens', valor: string) => {
    if (tipo === 'eventos') {
      onChange({ ...grupo, eventos: grupo.eventos.filter((v) => String(v) !== valor) });
    } else if (tipo === 'cidades') {
      onChange({ ...grupo, cidades: grupo.cidades.filter((v) => v !== valor) });
    } else {
      onChange({ ...grupo, origens: grupo.origens.filter((v) => v !== valor) });
    }
  };

  return (
    <div>
      <p className="mb-2 text-sm font-medium text-foreground-800">Para quem?</p>
      <div className="inline-flex rounded-full bg-background-100 p-1">
        <button
          type="button"
          onClick={() => {
            setModo('todos');
            onChange({ eventos: [], cidades: [], origens: [] });
          }}
          className={`aperta min-h-9 cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
            modo === 'todos'
              ? 'bg-background-50 text-primary-700 shadow-pcard'
              : 'text-foreground-600 hover:text-foreground-900'
          }`}
        >
          Todos
        </button>
        <button
          type="button"
          onClick={() => setModo('grupo')}
          className={`aperta min-h-9 cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
            modo === 'grupo'
              ? 'bg-background-50 text-primary-700 shadow-pcard'
              : 'text-foreground-600 hover:text-foreground-900'
          }`}
        >
          Escolher grupo
        </button>
      </div>

      {modo === 'grupo' && (
        <div className="mt-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <SeletorFiltro
              rotulo="Eventos"
              icone="ri-calendar-line"
              opcoes={opcoes.eventos}
              selecionados={grupo.eventos.map(String)}
              onChange={(v) => onChange({ ...grupo, eventos: v.map(Number) })}
              placeholderBusca="Buscar evento…"
            />
            <SeletorFiltro
              rotulo="Cidades"
              icone="ri-map-pin-line"
              opcoes={opcoes.cidades}
              selecionados={grupo.cidades}
              onChange={(v) => onChange({ ...grupo, cidades: v })}
              placeholderBusca="Buscar cidade…"
            />
            <SeletorFiltro
              rotulo="Tipo de pessoa"
              icone="ri-user-3-line"
              opcoes={opcoes.origens}
              selecionados={grupo.origens}
              onChange={(v) => onChange({ ...grupo, origens: v })}
              placeholderBusca="Buscar tipo…"
            />
          </div>

          {escolhidos.length === 0 ? (
            <p className="mt-3 text-sm text-foreground-500">
              Nada escolhido ainda — vale para todos.
            </p>
          ) : (
            <div className="mt-3 flex flex-wrap gap-2">
              {escolhidos.map((c) => (
                <span
                  key={`${c.tipo}-${c.valor}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 py-1 pl-3 pr-1.5 text-xs font-semibold text-secondary-700"
                >
                  {c.rotulo}
                  <button
                    type="button"
                    onClick={() => remover(c.tipo, c.valor)}
                    className="aperta flex h-5 w-5 cursor-pointer items-center justify-center rounded-full text-secondary-600 transition-colors hover:bg-secondary-200"
                    aria-label={`Remover ${c.rotulo}`}
                  >
                    <i className="ri-close-line" aria-hidden />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}